import { datadogLogs } from '@datadog/browser-logs';
import {
  getFullstoryConfig,
  isDomainDisabled,
  shouldInitializeFullstory,
} from './Fullstory/config';

/**
 * Initialize Fullstory browser SDK conditionally based on domain and configuration
 * Uses dynamic import to avoid loading the SDK when not needed
 */
const initializeFullstory = async (): Promise<void> => {
  try {
    // Check if Fullstory is enabled via domain
    if (isDomainDisabled()) {
      // eslint-disable-next-line no-console
      console.info('Fullstory: Initialization skipped');
      if (window.DATADOG_RUM_ENABLED) {
        datadogLogs.logger.info('Fullstory: Initialization skipped');
      }
      return;
    }
    // Check if Fullstory should be initialized
    if (!shouldInitializeFullstory()) {
      // eslint-disable-next-line no-console
      console.info('Fullstory: Initialization skipped based on org id');
      if (window.DATADOG_RUM_ENABLED) {
        datadogLogs.logger.info(
          'Fullstory: Initialization skipped based on org id'
        );
      }
      return;
    }

    // Get Fullstory configuration
    const config = getFullstoryConfig();
    if (!config) {
      // eslint-disable-next-line no-console
      console.warn('Fullstory: No valid configuration found');
      if (window.DATADOG_RUM_ENABLED) {
        datadogLogs.logger.warn('Fullstory: No valid configuration found');
      }
      return;
    }

    // Dynamically import Fullstory SDK
    const { init } = await import('@fullstory/browser');

    // Initialize Fullstory with configuration
    init({
      orgId: config.orgId,
      debug: config.debug,
      recordCrossDomainIFrames: config.recordCrossDomainIFrames,
      recordOnlyThisIFrame: config.recordOnlyThisIFrame,
      devMode: config.devMode,
    });

    // eslint-disable-next-line no-console
    console.info('Fullstory: Successfully initialized', {
      environment: config.environment,
      version: config.version,
      debug: config.debug,
    });
    if (window.DATADOG_RUM_ENABLED) {
      datadogLogs.logger.info('Fullstory: Successfully initialized', {
        environment: config.environment,
        version: config.version,
        debug: config.debug,
      });
    }
  } catch (error) {
    // Enhanced error handling for production environments
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    const errorStack = error instanceof Error ? error.stack : undefined;

    if (window.DATADOG_RUM_ENABLED) {
      datadogLogs.logger.error('Fullstory: Failed to initialize', {
        error: errorMessage,
        stack: errorStack,
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    }

    // Don't re-throw the error to prevent it from breaking the app
    // This ensures browser extensions or other interference don't crash the application
  }
};

initializeFullstory();
